/**
 * Вычисление формул в стиле Excel для модалки ВПР.
 * Поддержка: ссылки на ячейки (A1, Лист2!B3), диапазоны (A1:A10),
 * функции SUM, AVERAGE, COUNT, MIN, MAX, арифметика + - * / ().
 *
 * sheets = { "Лист1": { headers: string[], rows: string[][] }, ... }
 * Нумерация: строка 1 = первая строка данных (rows[0]), столбец A = 0.
 */

const COL_REF = /^([A-Za-z]+)(\d+)$/;

/** Буквы столбца в индекс: A=0, B=1, ..., Z=25, AA=26 */
export function columnLettersToIndex(letters) {
  if (!letters || !/^[A-Za-z]+$/.test(letters)) return -1;
  let n = 0;
  const s = letters.toUpperCase();
  for (let i = 0; i < s.length; i++) {
    n = n * 26 + (s.charCodeAt(i) - 64);
  }
  return n - 1;
}

/** Индекс столбца в буквы: 0=A, 26=AA */
export function columnIndexToLetters(index) {
  if (index < 0) return '';
  let s = '';
  let n = index + 1;
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

/**
 * Парсинг ссылки на ячейку: "A1" или "Лист2!A1".
 * Возвращает { sheetName, colIndex, rowIndex } или null.
 * rowIndex 1-based (1 = первая строка данных).
 */
export function parseCellRef(ref, currentSheetName) {
  if (!ref || typeof ref !== 'string') return null;
  const t = ref.trim();
  let sheetName = currentSheetName;
  let cellPart = t;
  const ex = t.indexOf('!');
  if (ex > 0) {
    const rawSheet = t.slice(0, ex).trim();
    if (rawSheet.startsWith("'") && rawSheet.endsWith("'")) {
      sheetName = rawSheet.slice(1, -1);
    } else {
      sheetName = rawSheet;
    }
    cellPart = t.slice(ex + 1).trim();
  }
  const m = cellPart.match(COL_REF);
  if (!m) return null;
  const col = columnLettersToIndex(m[1]);
  const row = parseInt(m[2], 10);
  if (col < 0 || row < 1) return null;
  return { sheetName, colIndex: col, rowIndex: row };
}

/**
 * Парсинг диапазона "A1:B10" или "Лист2!A1:B10".
 * Возвращает { sheetName, col1, row1, col2, row2 } или null.
 */
export function parseRangeRef(rangeStr, currentSheetName) {
  if (!rangeStr || typeof rangeStr !== 'string') return null;
  const t = rangeStr.trim();
  let sheetName = currentSheetName;
  let rangePart = t;
  const ex = t.indexOf('!');
  if (ex > 0) {
    const rawSheet = t.slice(0, ex).trim();
    sheetName = rawSheet.startsWith("'") && rawSheet.endsWith("'")
      ? rawSheet.slice(1, -1)
      : rawSheet;
    rangePart = t.slice(ex + 1).trim();
  }
  const colon = rangePart.indexOf(':');
  if (colon <= 0) return null;
  const start = parseCellRef(rangePart.slice(0, colon).trim(), sheetName);
  const end = parseCellRef(rangePart.slice(colon + 1).trim(), sheetName);
  if (!start || !end || start.sheetName !== end.sheetName) return null;
  return {
    sheetName: start.sheetName,
    col1: Math.min(start.colIndex, end.colIndex),
    row1: Math.min(start.rowIndex, end.rowIndex),
    col2: Math.max(start.colIndex, end.colIndex),
    row2: Math.max(start.rowIndex, end.rowIndex),
  };
}

/** Привести значение ячейки к числу для расчётов (пусто = 0). */
function toNumber(v) {
  if (v == null || v === '') return 0;
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  const s = String(v).trim().replace(',', '.');
  const n = parseFloat(s);
  return Number.isNaN(n) ? 0 : n;
}

/** Получить сырое значение ячейки из листа (без вычисления формул). */
function getRawCell(sheets, sheetName, colIndex, rowIndex) {
  const sheet = sheets[sheetName];
  if (!sheet || !sheet.rows) return null;
  const rows = sheet.rows;
  // rowIndex 1-based: 1 = rows[0]
  const row = rows[rowIndex - 1];
  if (!row) return null;
  const val = row[colIndex];
  return val === undefined || val === null ? '' : String(val).trim();
}

/**
 * Вычислить значение ячейки: если это формула (=...), вернуть результат, иначе сырое значение.
 * evaluating — Set ключей "sheetName!col:row" для обнаружения циклических ссылок.
 */
function getCellValue(sheets, sheetName, colIndex, rowIndex, evaluating) {
  const key = `${sheetName}!${colIndex}:${rowIndex}`;
  if (evaluating.has(key)) return { error: '#ЦИКЛ!' };
  const raw = getRawCell(sheets, sheetName, colIndex, rowIndex);
  if (raw === null) return null;
  if (raw === '') return 0; // для арифметики пустая ячейка = 0
  if (!raw.startsWith('=')) {
    const n = toNumber(raw);
    return n === 0 && raw !== '' && raw !== '0' ? raw : n; // число или строка
  }
  evaluating.add(key);
  try {
    const result = evaluateFormula(raw, sheetName, sheets, evaluating);
    return result;
  } finally {
    evaluating.delete(key);
  }
}

/**
 * Вычислить значения всех ячеек в диапазоне (для SUM, AVERAGE и т.д.).
 * Возвращает массив чисел (нечисловые пропускаются для SUM/AVERAGE, учитываются для COUNT).
 */
function getRangeValues(sheets, range, currentSheetName, evaluating) {
  const r = typeof range === 'string'
    ? parseRangeRef(range, currentSheetName)
    : range;
  if (!r) return [];
  const sheet = sheets[r.sheetName];
  if (!sheet || !sheet.rows) return [];
  const values = [];
  for (let row = r.row1; row <= r.row2; row++) {
    for (let col = r.col1; col <= r.col2; col++) {
      const v = getCellValue(sheets, r.sheetName, col, row, evaluating);
      if (v && typeof v === 'object' && v.error) return [{ error: v.error }];
      values.push(v);
    }
  }
  return values;
}

function evalSum(values) {
  let sum = 0;
  for (const v of values) {
    const n = toNumber(v);
    if (!Number.isNaN(n)) sum += n;
  }
  return sum;
}

function evalAverage(values) {
  const nums = values.map(toNumber).filter((n) => !Number.isNaN(n) && (n !== 0 || values.some((x) => toNumber(x) === 0)));
  if (nums.length === 0) return 0;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

function evalCount(values) {
  return values.filter((v) => v !== '' && v != null && String(v).trim() !== '').length;
}

function evalMin(values) {
  const nums = values.map(toNumber).filter((n) => !Number.isNaN(n));
  return nums.length === 0 ? 0 : Math.min(...nums);
}

function evalMax(values) {
  const nums = values.map(toNumber).filter((n) => !Number.isNaN(n));
  return nums.length === 0 ? 0 : Math.max(...nums);
}

function hasError(vals) {
  const err = vals.find((v) => v && typeof v === 'object' && v.error);
  return err ? err.error : null;
}

const FUNCTIONS = {
  SUM: (args, sheets, currentSheet, evaluating) => {
    let total = 0;
    for (const a of args) {
      const r = parseRangeRef(a, currentSheet);
      if (r) {
        const vals = getRangeValues(sheets, r, currentSheet, evaluating);
        const e = hasError(vals);
        if (e) return { error: e };
        total += evalSum(vals);
      } else {
        total += toNumber(a);
      }
    }
    return total;
  },
  AVERAGE: (args, sheets, currentSheet, evaluating) => {
    const all = [];
    for (const a of args) {
      const r = parseRangeRef(a, currentSheet);
      if (r) {
        const vals = getRangeValues(sheets, r, currentSheet, evaluating);
        const e = hasError(vals);
        if (e) return { error: e };
        all.push(...vals);
      } else all.push(toNumber(a));
    }
    return evalAverage(all);
  },
  COUNT: (args, sheets, currentSheet, evaluating) => {
    let c = 0;
    for (const a of args) {
      const r = parseRangeRef(a, currentSheet);
      if (r) {
        const vals = getRangeValues(sheets, r, currentSheet, evaluating);
        const e = hasError(vals);
        if (e) return { error: e };
        c += evalCount(vals);
      } else if (a !== '' && a != null) c += 1;
    }
    return c;
  },
  MIN: (args, sheets, currentSheet, evaluating) => {
    const all = [];
    for (const a of args) {
      const r = parseRangeRef(a, currentSheet);
      if (r) {
        const vals = getRangeValues(sheets, r, currentSheet, evaluating);
        const e = hasError(vals);
        if (e) return { error: e };
        all.push(...vals);
      } else all.push(toNumber(a));
    }
    return evalMin(all);
  },
  MAX: (args, sheets, currentSheet, evaluating) => {
    const all = [];
    for (const a of args) {
      const r = parseRangeRef(a, currentSheet);
      if (r) {
        const vals = getRangeValues(sheets, r, currentSheet, evaluating);
        const e = hasError(vals);
        if (e) return { error: e };
        all.push(...vals);
      } else all.push(toNumber(a));
    }
    return evalMax(all);
  },
};

function splitTopLevel(str, char) {
  const out = [];
  let depth = 0;
  let cur = '';
  for (let i = 0; i < str.length; i++) {
    const c = str[i];
    if (c === '(' || c === '[') depth++;
    else if (c === ')' || c === ']') depth--;
    else if (c === char && depth === 0) {
      out.push(cur);
      cur = '';
      continue;
    }
    cur += c;
  }
  out.push(cur);
  return out;
}

/**
 * Безопасное вычисление арифметического выражения (только числа, +, -, *, /, скобки).
 */
function evalArithmetic(expr) {
  const s = String(expr)
    .replace(/\s/g, '')
    .replace(/,/g, '.');
  const tokens = [];
  let i = 0;
  while (i < s.length) {
    if (s[i] === '(' || s[i] === ')' || s[i] === '+' || s[i] === '*' || s[i] === '/') {
      tokens.push(s[i++]);
      continue;
    }
    if (s[i] === '-') {
      const prev = tokens[tokens.length - 1];
      if (!prev || prev === '(' || prev === '+' || prev === '-' || prev === '*' || prev === '/') {
        tokens.push('neg');
        i++;
        continue;
      }
      tokens.push('-');
      i++;
      continue;
    }
    if (/[0-9.]/.test(s[i])) {
      let num = '';
      while (i < s.length && /[0-9.]/.test(s[i])) num += s[i++];
      const n = parseFloat(num);
      if (Number.isNaN(n)) return { error: '#ЧИСЛО!' };
      tokens.push(n);
      continue;
    }
    i++;
  }
  const rpn = toRPN(tokens);
  const result = evaluateRPN(rpn);
  return typeof result === 'number' && !Number.isNaN(result) ? result : { error: '#ЗНАЧ!' };
}

function toRPN(tokens) {
  const out = [];
  const op = [];
  const prec = { '+': 1, '-': 1, '*': 2, '/': 2, neg: 3 };
  for (const t of tokens) {
    if (typeof t === 'number') {
      out.push(t);
    } else if (t === 'neg') {
      op.push(t);
    } else if (t === '(') {
      op.push(t);
    } else if (t === ')') {
      while (op.length && op[op.length - 1] !== '(') out.push(op.pop());
      op.pop();
      if (op[op.length - 1] === 'neg') out.push(op.pop());
    } else if (prec[t] != null) {
      while (op.length && op[op.length - 1] !== '(' && prec[op[op.length - 1]] >= prec[t]) {
        out.push(op.pop());
      }
      op.push(t);
    }
  }
  while (op.length) out.push(op.pop());
  return out;
}

function evaluateRPN(rpn) {
  const stack = [];
  for (const t of rpn) {
    if (typeof t === 'number') {
      stack.push(t);
    } else if (t === 'neg') {
      if (stack.length < 1) return NaN;
      stack.push(-stack.pop());
    } else if (t === '+') {
      if (stack.length < 2) return NaN;
      stack.push(stack.pop() + stack.pop());
    } else if (t === '-') {
      if (stack.length < 2) return NaN;
      const b = stack.pop();
      const a = stack.pop();
      stack.push(a - b);
    } else if (t === '*') {
      if (stack.length < 2) return NaN;
      stack.push(stack.pop() * stack.pop());
    } else if (t === '/') {
      if (stack.length < 2) return NaN;
      const b = stack.pop();
      const a = stack.pop();
      stack.push(b === 0 ? NaN : a / b);
    }
  }
  return stack.length === 1 ? stack[0] : NaN;
}

/**
 * Главная функция: вычислить формулу (строка, начинающаяся с "=").
 * sheets = { sheetName: { headers, rows } }
 * currentSheetName — лист, в ячейке которого записана формула.
 * evaluating — Set для обнаружения циклических ссылок (создаётся при первом вызове).
 */
export function evaluateFormula(formula, currentSheetName, sheets, evaluating = new Set()) {
  if (!formula || typeof formula !== 'string') return '';
  const raw = formula.trim();
  if (!raw.startsWith('=')) return raw;
  const expr = raw.slice(1).trim();
  if (!expr) return '';

  let step = expr;

  // 1) Заменить функции на значения
  const funcRegex = /(SUM|AVERAGE|COUNT|MIN|MAX)\s*\(([^()]*(?:\([^()]*\)[^()]*)*)\)/gi;
  let fnMatch;
  const replacements = [];
  while ((fnMatch = funcRegex.exec(expr)) !== null) {
    const fn = fnMatch[1].toUpperCase();
    const argStr = fnMatch[2];
    const args = splitTopLevel(argStr, ',').map((a) => a.trim());
    let value = 0;
    try {
      const resolved = args.map((arg) => {
        const range = parseRangeRef(arg, currentSheetName);
        if (range) {
          const vals = getRangeValues(sheets, range, currentSheetName, evaluating);
          if (fn === 'SUM') return evalSum(vals);
          if (fn === 'AVERAGE') return evalAverage(vals);
          if (fn === 'COUNT') return evalCount(vals);
          if (fn === 'MIN') return evalMin(vals);
          if (fn === 'MAX') return evalMax(vals);
          return 0;
        }
        return toNumber(arg);
      });
      value = FUNCTIONS[fn](resolved, sheets, currentSheetName, evaluating);
    } catch (e) {
      value = '#ОШИБКА!';
    }
    const toStr = value && typeof value === 'object' && value.error ? value.error : String(value);
    replacements.push({ from: fnMatch[0], to: toStr });
  }
  for (let i = replacements.length - 1; i >= 0; i--) {
    const toVal = replacements[i].to;
    step = step.split(replacements[i].from).join(
      toVal.startsWith('#') ? toVal : `(${toVal})`
    );
  }
  if (/^#\S+!?$/.test(step.trim())) return step.trim();

  // 2) Заменить ссылки на ячейки на значения
  const cellRefRegex = /(?:^|[^\w])((?:'[^']*'|[A-Za-zА-Яа-я0-9_]+)!)?([A-Za-z]+)(\d+)(?=[^\w]|$)/g;
  step = step.replace(cellRefRegex, (full, sheetPart, colLetters, rowNum) => {
    const sheetName = sheetPart
      ? sheetPart.replace(/!$/, '').replace(/^'|'$/g, '').trim()
      : currentSheetName;
    const col = columnLettersToIndex(colLetters);
    const row = parseInt(rowNum, 10);
    if (col < 0 || row < 1) return full;
    const val = getCellValue(sheets, sheetName, col, row, evaluating);
    if (val != null && typeof val === 'object' && val.error) return val.error;
    const num = toNumber(val);
    if (typeof val === 'string' && Number.isNaN(num) && val !== '') return '0';
    return String(num);
  });

  // 3) Вычислить арифметику
  const result = evalArithmetic(step);
  if (result && typeof result === 'object' && result.error) return result.error;
  return typeof result === 'number' ? result : step;
}

/**
 * Получить отображаемое значение ячейки: если формула — результат, иначе сырое значение.
 */
export function getDisplayValue(sheets, sheetName, colIndex, rowIndex, evaluating = new Set()) {
  const raw = getRawCell(sheets, sheetName, colIndex, rowIndex);
  if (raw === null || raw === '') return '';
  if (raw.startsWith('=')) {
    const result = evaluateFormula(raw, sheetName, sheets, evaluating);
    return result === '' ? '' : String(result);
  }
  return raw;
}
